import { useCallback, useMemo, memo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar, Gauge, Fuel, Settings } from "lucide-react";
import { ListingOverview } from "@/types/listing";
import { useListing } from "@/hooks/useListing";
import { SaleTypeBadge } from "@/components/ui/SaleTypeBadge";
import { useIsMobile } from "@/hooks/use-mobile";
import { formatPriceWithReduction } from "@/utils/priceDisplay";
import { formatMileage } from "@/utils/listingUtils";
import { fuelTypeLabels, transmissionTypeLabels } from "@/i18n/labels/vehicleLabels";
import { indexLabels } from "@/i18n";
import { useLanguage } from "@/context/LanguageContext";
import { VehicleFuelType, TransmissionType } from "@/types/enums";
import { useNativePlatform } from "@/hooks/useNativePlatform";

import { navigateToListingDetail } from "@/lib/native/listingNavigation";
import { cn } from "@/lib/utils";
import { NativeImage } from "@/components/native/NativeImage";

interface OtherListingsCarouselProps {
  currentListingId?: string;
  title?: string;
  onCarClick?: (listing: ListingOverview) => void;
}

// Memoized carousel item component for better performance
const CarouselItemCard = memo(
  ({
    listing,
    index,
    onClick,
    formatPrice,
    formatPriceWithReduction,
    formatFuelType,
    formatTransmission,
    isMobile,
    isNative,
  }: {
    listing: ListingOverview;
    index: number;
    onClick: (e: React.MouseEvent) => void;
    formatPrice: (price: number) => string;
    formatPriceWithReduction: (currentPrice: number, originalPrice?: number) => { price: string; showReduction: boolean; reductionText?: string };
    formatFuelType: (fuelType?: string) => string;
    formatTransmission: (transmission?: string) => string;
    isMobile: boolean;
    isNative: boolean;
  }) => {
    // Only use animations on desktop for better mobile performance
    const animationStyle = !isMobile
      ? {
          opacity: 0,
          transform: "translateX(100%)",
          animation: `
      fadeIn 0.5s ease-out ${index * 100}ms forwards,
      slideIn 0.8s ease-out ${index * 100}ms forwards
    `,
        }
      : {};

    return (
      <CarouselItem
        key={`${listing.id}-${index}`}
        className={cn(
          isNative || isMobile
            ? "flex !basis-[48%] !max-w-[48%] !pl-1"
            : "flex !basis-2/3 !pl-2",
          !isNative && !isMobile && "md:!basis-1/3 lg:!basis-1/5",
        )}
        style={animationStyle}
        onClick={onClick}
      >
        <Card className="cursor-pointer h-full flex flex-col overflow-hidden">
          <CardContent className="p-0 flex flex-col h-full">
            <div
              className="relative h-28 w-full shrink-0 overflow-hidden rounded-t-lg bg-black"
              data-listing-hero-image
            >
              <NativeImage
                src={listing.thumbnail}
                alt={listing.title}
                className="absolute inset-0 w-full h-full object-cover object-center"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute top-2 left-2">
                <SaleTypeBadge
                  saleType={
                    listing.listingType === "auction" ? "auction" : "direct-purchase"
                  }
                  className="text-[10px]"
                />
              </div>
            </div>
            <div className="p-2">
              <h3
                className="font-medium text-sm mb-1 line-clamp-2 leading-tight"
                style={{ minHeight: "2.5rem" }}
              >
                {listing.title}
              </h3>
              <div className="mb-2">
                {listing.listingType === "direct-purchase" && listing.directPurchaseDetails ? (
                  (() => {
                    const priceInfo = formatPriceWithReduction(
                      listing.directPurchaseDetails.currentPrice || 0,
                      listing.directPurchaseDetails.originalPrice
                    );
                    return (
                      <>
                        <p className={`font-bold text-base ${priceInfo.showReduction ? "text-red-700 dark:text-red-400" : "text-black dark:text-white"}`}>
                          {priceInfo.price}
                        </p>
                        {priceInfo.showReduction && priceInfo.reductionText && (
                          <p className="text-xs text-green-600 mt-0.5">
                            {priceInfo.reductionText}
                          </p>
                        )}
                      </>
                    );
                  })()
                ) : (
                  <p className="text-black dark:text-white font-bold text-base">
                    {formatPrice(
                      listing.auctionDetails?.currentBid ||
                        listing.auctionDetails?.startingPrice ||
                        0
                    )}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
                {listing.vehicleDetails?.mileage != null && listing.vehicleDetails.mileage > 0 ? (
                  <div className="flex items-center gap-1">
                    <Gauge className="w-3 h-3 flex-shrink-0" />
                    <span>{formatMileage(listing.vehicleDetails.mileage)}</span>
                  </div>
                ) : null}
                {listing.vehicleDetails?.fuelType ? (
                  <div className="flex items-center gap-1">
                    <Fuel className="w-3 h-3 flex-shrink-0" />
                    <span>{formatFuelType(listing.vehicleDetails.fuelType)}</span>
                  </div>
                ) : null}
                {listing.vehicleDetails?.transmission ? (
                  <div className="flex items-center gap-1">
                    <Settings className="w-3 h-3 flex-shrink-0" />
                    <span>{formatTransmission(listing.vehicleDetails.transmission)}</span>
                  </div>
                ) : null}
                {listing.vehicleDetails?.year ? (
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3 flex-shrink-0" />
                    <span>{listing.vehicleDetails.year}</span>
                  </div>
                ) : null}
              </div>
            </div>
          </CardContent>
        </Card>
      </CarouselItem>
    );
  }
);

CarouselItemCard.displayName = "CarouselItemCard";

const OtherListingsCarousel = ({
  currentListingId,
  title,
  onCarClick,
}: OtherListingsCarouselProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { isNative } = useNativePlatform();
  const { language } = useLanguage();
  const labels = indexLabels(language);

  // Fetch public listings for carousel (popular/active listings)
  const { getPublicListingsCarousel } = useListing();
  const publicListingsQuery = getPublicListingsCarousel(20); // Fetch more to allow filtering

  const listings = useMemo(() => {
    if (!publicListingsQuery.data) return [];

    // Filter out current listing and only show ACTIVE listings
    const filteredListings = publicListingsQuery.data.filter(
      (listing) => listing.id !== currentListingId && listing.status === "active"
    );

    // Take first 8 (they're already ordered by popularity from backend)
    return filteredListings.slice(0, 8);
  }, [publicListingsQuery.data, currentListingId]);

  // Navigation handler for carousel items
  const handleCarClick = useCallback(
    (listing: ListingOverview, e: React.MouseEvent) => {
      if (onCarClick) {
        onCarClick(listing);
        return;
      }

      const currentPath = location.pathname;
      let fromState;

      if (
        location.state?.from &&
        (location.state.from === "/orders" ||
          location.state.from === "/my-auctions" ||
          location.state.from === "/favorites" ||
          location.state.from.startsWith("/auctions/"))
      ) {
        fromState = location.state.from;
      } else {
        fromState = currentPath;
      }

      void navigateToListingDetail(navigate, listing, {
        from: fromState,
        fromScrollY: window.scrollY,
        heroImageEl: (e.currentTarget as HTMLElement).querySelector(
          "[data-listing-hero-image]",
        ) as HTMLElement | null,
        thumbnailUrl: listing.thumbnail,
      });
    },
    [navigate, location.pathname, location.state, onCarClick],
  );

  // Format price for display - memoized
  const formatPrice = useCallback((price: number) => {
    return new Intl.NumberFormat("sv-SE", {
      style: "currency",
      currency: "SEK",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  }, []);

  const formatFuelType = useCallback((fuelType?: string) => {
    if (!fuelType) return "-";
    const fuelLabels = fuelTypeLabels(language);
    const key = fuelType.toUpperCase() as VehicleFuelType;
    return fuelLabels[key] || fuelType;
  }, [language]);

  const formatTransmission = useCallback((transmission?: string) => {
    if (!transmission) return "-";
    const transLabels = transmissionTypeLabels(language);
    const key = transmission.toUpperCase() as TransmissionType;
    return transLabels[key] || transmission;
  }, [language]);

  // Format price with reduction - memoized
  const formatPriceWithReductionCallback = useCallback((currentPrice: number, originalPrice?: number) => {
    return formatPriceWithReduction(currentPrice, originalPrice);
  }, []);

  // Get border color based on listing type - memoized
  const getBorderColor = useCallback((listing: ListingOverview) => {
    if (listing.listingType === "auction") {
      return "border-b-orange-300 dark:border-b-orange-600";
    } else {
      return "border-b-green-300 dark:border-b-green-600";
    }
  }, []);

  if (listings.length === 0) {
    return null;
  }

  return (
    <div className={isNative ? "mt-4 native-listing-carousel" : "mt-16"}>
      {title != null ? (
        <h3 className="text-center text-xl md:text-2xl font-bold mb-4">
          {title}
        </h3>
      ) : (
        <h2 className="text-lg font-bold mb-4 text-primary">
          {labels.carousel.discoverOther}
        </h2>
      )}

      <Carousel
        opts={{
          align: "start",
          loop: true,
          skipSnaps: false,
          containScroll: "trimSnaps",
        }}
        className="w-full"
      >
        <CarouselContent
          className={isNative || isMobile ? "!-ml-1" : "!-ml-2"}
        >
          {listings.map((listing, index) => (
            <CarouselItemCard
              key={`${listing.id}-${index}`}
              listing={listing}
              index={index}
              onClick={(e) => handleCarClick(listing, e)}
              formatPrice={formatPrice}
              formatPriceWithReduction={formatPriceWithReduction}
              formatFuelType={formatFuelType}
              formatTransmission={formatTransmission}
              isMobile={isMobile}
              isNative={isNative}
            />
          ))}
        </CarouselContent>
        <CarouselPrevious
          className={cn(
            "md:flex h-6 w-6",
            isNative || isMobile ? "left-1" : "-left-3",
          )}
        />
        <CarouselNext
          className={cn(
            "md:flex h-6 w-6",
            isNative || isMobile ? "right-1" : "-right-3",
          )}
        />
      </Carousel>
    </div>
  );
};

export default OtherListingsCarousel;
